/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import { Helmet } from 'react-helmet'
import PropTypes from 'prop-types'
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import { Button } from 'msp-components'
import { useNavigation } from 'msp-integrations'

// <Error> is rendered when:
//
// 1. A user requests a page that is not routable from `app/routes.jsx`.
// 2. A routed component throws an error in `getProps()`.
// 3. A routed component throws an error in `render()`.
//
// It must not throw an error. Keep it as simple as possible.

const Error = (props) => {
  const { message, stack } = props
  const navigate = useNavigation()

  const title = "This page isn't working"
  return (
    <div id='sf-app'>
      <div className='searchPage-main'>
        <Helmet>
          <title>{title}</title>
          <link rel='stylesheet' href={getAssetUrl('static/css/styles.css')} />
        </Helmet>
        <div className='searchPage-container'>
          <p className={'w-full p-1 text-center'}>
            An error has occurred. Try refreshing the page or if you need
            immediate help please contact support.
          </p>
          {message && <p className={'w-full p-1 text-center'}>{message}</p>}
          {stack && (
            <>
              <p className={'w-full p-1 text-center'}>Stack Trace:</p>
              <p className={'w-full p-1 text-center'}>{stack}</p>
            </>
          )}
          <div className='cc-grid-button-wrapper p-4'>
            <Button
              className='cc-grid-showAllBtn'
              variant={'outline'}
              type={'primary'}
              iconName={'ArrowRightStick'}
              iconPosition='right'
              onClick={() => navigate('/')}
            >
              Go to home
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

Error.propTypes = {
  // JavaScript error stack trace: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/stack
  stack: PropTypes.string,
  // HTTP status code: https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
  status: PropTypes.number,
  // A description of the error, if available
  message: PropTypes.string
}

export default Error
